import React, {memo, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Popover, PopoverTrigger} from 'components/ui/Popover';
import DropdownMenuContent from './DropdownMenuContent';
import DropdownMenuItem from './DropdownMenuItem';
import DropdownMenuTrigger from './DropdownMenuTrigger';

/**
 * DropdownMenu - Compound component for navigation/action menus
 * Built on Popover with keyboard navigation support
 *
 * @typedef {Object} DropdownMenuProps
 * @property {React.ReactNode|Function} children - DropdownMenuTrigger and DropdownMenuContent, or function (close) => ReactNode
 * @property {React.ReactNode} trigger - Trigger element (button, etc.)
 * @property {Array} items - Array of items for keyboard navigation
 * @property {Function} [getItemId] - Function to get item ID: (item, index) => string
 * @property {Function} [getItemDisabled] - Function to check if item is disabled: (item, index) => boolean
 * @property {Function} onItemClick - Callback when item is clicked: (item, index) => void
 * @property {string} [placement] - Popper placement (default: 'bottom-start')
 * @property {string} [size] - Size variant for menu items: 'xs', 'sm', 'md', 'lg' (default: 'md')
 * @property {string} [className] - Additional CSS classes for container
 */
const DropdownMenu = memo(
	({
		children,
		trigger,
		items = [],
		getItemId = (item, index) => `item-${index}`,
		getItemDisabled = (item, index) => item?.disabled === true,
		onItemClick,
		placement = 'bottom-start',
		size = 'md',
		className,
		...props
	}) => {
		const [open, setOpen] = useState(false);
		const [highlightedIndex, setHighlightedIndex] = useState(-1);

		const handleOpenChange = useCallback(newOpen => {
			setOpen(newOpen);
			if (!newOpen) {
				// Reset highlight when closing
				setHighlightedIndex(-1);
			}
		}, []);

		const handleItemActivate = useCallback(
			(item, index) => {
				if (!getItemDisabled(item, index)) {
					onItemClick?.(item, index);
					// Close menu after selection
					setOpen(false);
				}
			},
			[onItemClick, getItemDisabled],
		);

		const close = useCallback(() => {
			setOpen(false);
		}, []);

		// Handle children as function (render prop pattern)
		const resolvedChildren = typeof children === 'function' ? children(close) : children;

		// Find DropdownMenuContent in children and inject props
		const childrenArray = React.Children.toArray(resolvedChildren);
		const menuContentChild = childrenArray.find(
			child =>
				React.isValidElement(child) &&
				(child.type === DropdownMenuContent ||
					child.type?.displayName === 'DropdownMenuContent'),
		);

		const menuContentWithProps = menuContentChild
			? React.cloneElement(menuContentChild, {
					items,
					highlightedIndex,
					setHighlightedIndex,
					onItemActivate: handleItemActivate,
					getItemDisabled,
					size,
			  })
			: null;

		// If no DropdownMenuContent found in children, create default one
		const defaultContent = !menuContentChild ? (
			<DropdownMenuContent
				items={items}
				highlightedIndex={highlightedIndex}
				setHighlightedIndex={setHighlightedIndex}
				onItemActivate={handleItemActivate}
				getItemDisabled={getItemDisabled}
				size={size}
			>
				{childrenArray.filter(
					child => !React.isValidElement(child) || child.type !== DropdownMenuContent,
				)}
			</DropdownMenuContent>
		) : null;

		return (
			<Popover
				open={open}
				onOpenChange={handleOpenChange}
				placement={placement}
				className={className}
				{...props}
			>
				{trigger && <PopoverTrigger asChild>{trigger}</PopoverTrigger>}
				{/* DropdownMenuContent renders its own portal */}
				{menuContentWithProps || defaultContent}
			</Popover>
		);
	},
);

DropdownMenu.propTypes = {
	children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
	trigger: PropTypes.node,
	items: PropTypes.array,
	getItemId: PropTypes.func,
	getItemDisabled: PropTypes.func,
	onItemClick: PropTypes.func,
	placement: PropTypes.string,
	size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
	className: PropTypes.string,
};

DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;
export {DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger};
